import Phaser from 'phaser';
import type { PowerupSpawnSpec, WaterBottleSpawnSpec, EnemySpawnSpec, StrictPaths } from '../types/StrictLevelData';
import { getGameContext } from './context';
import { SFX } from '../audio/AudioKeys';
import { Zombie } from '../objects/Zombie';
import Projectile from '../objects/Projectile';

// Spawner expects normalized Strict* specs. Presence/default validation is performed by LevelDataNormalizer.

export interface SpawnedGroups {
  pistolPowerups: Phaser.Physics.Arcade.Group;
  waterBottles: Phaser.Physics.Arcade.Group;
  enemiesGroup: Phaser.Physics.Arcade.Group;
}

/**
 * Spawner centralizes all map-entity spawning and related collider/overlap wiring.
 * It is a thin, scene-bound utility that keeps GameScene lean and declarative.
 *
 * Responsibilities:
 * - Spawn pistol powerups (including four-corner fallback)
 * - Spawn water bottles
 * - Spawn enemies and basic visuals
 * - Wire overlaps/colliders related to spawned entities
 * - Provide a zombie respawn helper used by projectile/enemy interactions
 */
export class Spawner {
  private scene: Phaser.Scene;
  private wallLayer: Phaser.Tilemaps.TilemapLayer;
  private map: Phaser.Tilemaps.Tilemap;

  // Exposed groups created/managed by the spawner
  public pistolPowerups!: Phaser.Physics.Arcade.Group;
  public waterBottles!: Phaser.Physics.Arcade.Group;
  public enemiesGroup!: Phaser.Physics.Arcade.Group;

  constructor(scene: Phaser.Scene, map: Phaser.Tilemaps.Tilemap, wallLayer: Phaser.Tilemaps.TilemapLayer) {
    this.scene = scene;
    this.map = map;
    this.wallLayer = wallLayer;

    // Create groups up-front so callers can use them immediately
    this.pistolPowerups = this.scene.physics.add.group({
      allowGravity: false,
      immovable: true,
      maxSize: -1, // unlimited unless constrained elsewhere
    });

    // Use a dynamic group to leverage get() pooling; we will make bodies static-like via enable flags
    this.waterBottles = this.scene.physics.add.group({
      allowGravity: false,
      immovable: true,
      maxSize: -1,
    });

    // Enemies pool; ensure instances are Zombie class to guarantee methods
    this.enemiesGroup = this.scene.physics.add.group({
      allowGravity: false,
      immovable: false,
      maxSize: -1,
      classType: Zombie,
      runChildUpdate: true,
    });

    // Enemies collide with walls
    this.scene.physics.add.collider(this.enemiesGroup, this.wallLayer);
  }

  /**
   * Spawn powerups using normalized Strict specs, pooled via group.get().
   */
  spawnPowerupsFromConfig(configs: PowerupSpawnSpec[]): void {
    const ensureTween = (obj: Phaser.GameObjects.GameObject, rotate?: boolean) => {
      if (!rotate) return;
      this.scene.tweens.add({
        targets: obj,
        duration: 800,
        yoyo: true,
        repeat: -1,
        scale: 1.1,
        ease: 'Sine.easeInOut',
      });
    };

    const revivePickup = (obj: Phaser.GameObjects.GameObject) => {
      obj.setActive(true);
      (obj as any).setVisible?.(true);
      const body = (obj as any).body as Phaser.Physics.Arcade.Body | undefined;
      if (body) {
        body.enable = true;
        body.setAllowGravity(false);
        body.setImmovable(true);
      }
      (obj as any).setDepth?.(3);
    };

    configs.forEach((c) => {
      if (c.type !== 'Pistol') return;

      // Try sprite keys first; fall back to a rectangle marker
      const tryKeys = ['weapon_gun', 'manBlue_gun'];

      let pickup: Phaser.GameObjects.GameObject | null = null;
      for (const k of tryKeys) {
        pickup = this.pistolPowerups.get(c.x, c.y, k) as Phaser.GameObjects.GameObject | null;
        if (pickup) break;
      }
      if (!pickup) {
        // Create a rectangle placeholder via factory once, then add to group
        const rect = this.scene.add.rectangle(c.x, c.y, 16, 8, 0xffe066).setDepth(3);
        this.scene.physics.add.existing(rect, true);
        this.pistolPowerups.add(rect as unknown as Phaser.GameObjects.GameObject, true);
        pickup = rect as unknown as Phaser.GameObjects.GameObject;
      }

      revivePickup(pickup);
      ensureTween(pickup, c.rotate);
    });
  }

  /**
   * Spawn water bottles using pooled group.get(); treat as static for overlaps and respawn.
   */
  spawnWaterBottlesFromConfig(
    configs: WaterBottleSpawnSpec[],
    getPlayer: () => Phaser.GameObjects.GameObject & { body?: Phaser.Physics.Arcade.Body },
    getWaterSeconds: () => number,
    setWaterSeconds: (v: number) => void
  ): void {
    const ensureTween = (obj: Phaser.GameObjects.GameObject, rotate?: boolean) => {
      if (!rotate) return;
      this.scene.tweens.add({
        targets: obj,
        duration: 900,
        yoyo: true,
        repeat: -1,
        scale: 1.1,
        ease: 'Sine.easeInOut',
      });
    };

    const reviveStaticLike = (obj: Phaser.GameObjects.GameObject) => {
      obj.setActive(true);
      (obj as any).setVisible?.(true);
      let body = (obj as any).body as Phaser.Physics.Arcade.StaticBody | Phaser.Physics.Arcade.Body | undefined;
      if (!body) {
        this.scene.physics.add.existing(obj, true);
        body = (obj as any).body;
      }
      if (body) {
        // Ensure it's treated as static for overlaps
        if ('setAllowGravity' in body) (body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
        (body as any).enable = true;
      }
      (obj as any).setDepth?.(3);
    };

    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

    configs.forEach((c) => {
      // Create or reuse a pooled circle via a special textureless object
      // Since group.get() needs a key, we create a Shape once when missing.
      let water: Phaser.GameObjects.GameObject | null = this.waterBottles.get(c.x, c.y, undefined) as any;
      if (!water) {
        const circle = this.scene.add.circle(c.x, c.y, 8, 0x66ccff).setDepth(3);
        this.scene.physics.add.existing(circle, true);
        this.waterBottles.add(circle as unknown as Phaser.GameObjects.GameObject, true);
        water = circle as unknown as Phaser.GameObjects.GameObject;
      }

      reviveStaticLike(water);
      ensureTween(water, c.rotate);

      const heal = c.heal;
      const onPickup = () => {
        const player = getPlayer() as any;
        const hasHealth = typeof player.health === 'number' && typeof player.maxHealth === 'number';
        if (hasHealth) {
          player.health = clamp(player.health + heal, 0, player.maxHealth);
        } else {
          const newVal = clamp(getWaterSeconds() + Math.floor(heal / 5), 0, 3600);
          setWaterSeconds(newVal);
        }

        // SFX: pickup
        try {
          const ctx = getGameContext(this.scene as any);
          ctx.sound?.playSfx(SFX.Pickup, { volume: 0.8 });
        } catch { }

        water!.setActive(false);
        (water as any).setVisible?.(false);
        const body = (water as any).body as Phaser.Physics.Arcade.StaticBody | Phaser.Physics.Arcade.Body | undefined;
        if (body) (body as any).enable = false;

        if (c.respawn && c.respawn > 0) {
          this.scene.time.delayedCall(c.respawn, () => {
            reviveStaticLike(water!);
          });
        } else {
          this.scene.time.delayedCall(0, () => (water as any).destroy?.());
        }
      };

      this.scene.physics.add.overlap(getPlayer(), water as any, onPickup, undefined, this.scene);
    });
  }

  /**
   * Spawn enemies and attach basic visuals/patrol metadata using pooled get().
   */
  spawnEnemiesFromConfig(configs: EnemySpawnSpec[], paths: StrictPaths): void {
    const reviveEnemy = (obj: Phaser.GameObjects.GameObject) => {
      obj.setActive(true);
      (obj as any).setVisible?.(true);
      const body = (obj as any).body as Phaser.Physics.Arcade.Body | undefined;
      if (body) {
        body.enable = true;
        body.setAllowGravity(false);
        body.setImmovable(false);
      }
      (obj as any).setDepth?.(4);
    };

    configs.forEach((cfg) => {
      // Get a pooled Zombie instance (classType ensures Zombie)
      let enemy = this.enemiesGroup.get(cfg.x, cfg.y) as unknown as Phaser.GameObjects.GameObject | null;

      if (!enemy) {
        // Create when pool empty
        const z = new Zombie(this.scene, cfg.x, cfg.y, 'zombie');
        this.enemiesGroup.add(z, true);
        enemy = z;
      } else {
        // If pool returned a generic object for any reason, replace with Zombie
        if (!(enemy instanceof Zombie)) {
          const z = new Zombie(this.scene, cfg.x, cfg.y, 'zombie');
          // Remove wrong-type instance to avoid leaks
          (enemy as any).destroy?.();
          this.enemiesGroup.add(z, true);
          enemy = z;
        } else {
          // Ensure correct texture for revived zombie
          (enemy as Zombie).setTexture('zombie');
          (enemy as any).setPosition?.(cfg.x, cfg.y);
        }
      }

      reviveEnemy(enemy);

      // Collider against walls (ensure it's set even for revived objects)
      this.scene.physics.add.collider(enemy as any, this.wallLayer);

      // Gentle breathing tween
      this.scene.tweens.add({
        targets: enemy,
        duration: 900,
        scale: 1.05,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      // Attach patrol metadata for AI systems
      const patrolPath = cfg.patrolPathId != null ? paths.byId.get(cfg.patrolPathId)! : undefined;
      (enemy as any).patrolData = patrolPath
        ? { path: patrolPath, loop: cfg.patrolLoop, wait: cfg.patrolWait }
        : undefined;
    });
  }

  /**
   * Wire pistol pickup overlap to mutate the provided player instance.
   */
  wirePistolPickupOverlap(getPlayer: () => { hasPistol: boolean; shotsRemaining: number } & Phaser.GameObjects.GameObject): void {
    const onPickupPistol = (_playerObj: Phaser.GameObjects.GameObject, pickupObj: Phaser.GameObjects.GameObject) => {
      if (!pickupObj.active) return;

      // Delegate pistol granting (state + SFX) to Player.grantPistol
      const player = getPlayer() as unknown as Phaser.GameObjects.GameObject & { grantPistol?: (shots?: number) => void };
      (player as any)?.grantPistol?.(5);

      // Deactivate the pickup safely
      (pickupObj as any).setActive?.(false);
      (pickupObj as any).setVisible?.(false);
      const body = (pickupObj as any).body as Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | undefined;
      if (body) body.enable = false;
    };

    this.scene.physics.add.overlap(
      getPlayer() as any,
      this.pistolPowerups,
      onPickupPistol as unknown as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this.scene
    );
  }

  /**
   * Wire player vs enemy overlap in Spawner, delegating the hit handling to the provided callback.
   * Keeps physics setup centralized while preserving scene-specific game-over logic.
   */
  wirePlayerEnemyOverlap(
    getPlayer: () => Phaser.GameObjects.GameObject,
    onPlayerHit: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback
  ): void {
    this.scene.physics.add.overlap(
      getPlayer() as any,
      this.enemiesGroup,
      onPlayerHit,
      undefined,
      this.scene
    );
  }

  /**
   * Wire projectile–enemy overlap.
   * Delegates the hit handling to the provided onHit callback, which receives (projectile, enemy).
   */
  wireProjectileEnemyOverlap(
    projectiles: Phaser.Physics.Arcade.Group,
    onHit: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback
  ): void {
    this.scene.physics.add.overlap(
      projectiles,
      this.enemiesGroup,
      onHit,
      undefined,
      this.scene
    );
  }

/**
 * Convenience: wire both projectile–enemy and player–enemy overlaps in one place.
 * Respawn is always handled here to keep logic centralized.
 */
wireAllEnemyOverlaps(
  projectiles: Phaser.Physics.Arcade.Group,
  getPlayer: () => Phaser.GameObjects.GameObject,
  onPlayerHit: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
  respawnDelayMs: number = 10000
): void {
  // Projectile vs enemy -> provide onHit that mirrors previous internal logic
  this.scene.physics; // no-op to keep scene type context accessible

  const onProjectileHitEnemy: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = ((obj1: any, obj2: any) => {
    // Determine which arg is the projectile vs enemy defensively
    const isProj = (o: any) =>
      o && (o instanceof Projectile || (o?.constructor?.name === 'Projectile') || typeof o?.initVelocity === 'function');
    const bullet = isProj(obj1) ? (obj1 as Projectile) : (isProj(obj2) ? (obj2 as Projectile) : (obj1 as Projectile));
    const enemyObj  = bullet === obj1 ? obj2 : obj1;

    // Verbose diagnostics for collisions
    try {
      // eslint-disable-next-line no-console
      console.log('[Spawner] onProjectileHitEnemy', {
        obj1Type: (obj1 as any)?.constructor?.name,
        obj2Type: (obj2 as any)?.constructor?.name,
        bulletType: (bullet as any)?.constructor?.name,
        enemyType: (enemyObj as any)?.constructor?.name,
        enemyActive: !!(enemyObj as any)?.active,
        enemyVisible: !!(enemyObj as any)?.visible,
        enemyHasDisableBody: typeof (enemyObj as any)?.disableBody === 'function',
        enemyHasEnableBody: typeof (enemyObj as any)?.enableBody === 'function',
        enemyHasHandleProjectileHit: typeof (enemyObj as any)?.handleProjectileHit === 'function'
      });
    } catch {}

    // Guard: ensure we have a Zombie-like object with handleProjectileHit before calling
    const hasHandler = enemyObj && typeof (enemyObj as any).handleProjectileHit === 'function';
    if (!hasHandler) {
      try {
        console.warn('[Spawner] Projectile-Enemy overlap: enemy object lacks handleProjectileHit', {
          enemyType: enemyObj?.constructor?.name,
          enemyKeys: enemyObj ? Object.keys(enemyObj) : null,
          obj1Type: (obj1 as any)?.constructor?.name,
          obj2Type: (obj2 as any)?.constructor?.name
        });
      } catch {}
      if (bullet && (bullet as any).destroy) {
        try { (bullet as any).destroy(); } catch {}
      }
      return;
    }

    // Immediately disable/hide the enemy locally to ensure disappearance
    const anyEnemy = enemyObj as any;
    if (typeof anyEnemy.disableBody === 'function') {
      try { anyEnemy.disableBody(true, true); } catch {}
    } else {
      anyEnemy.setActive?.(false);
      anyEnemy.setVisible?.(false);
    }

    // Safe call with explicit respawn delegate
    (enemyObj as unknown as Zombie).handleProjectileHit(bullet, {
      onRespawn: (z: Phaser.GameObjects.GameObject) => {
        this.respawnZombieAtCameraCenter(z, respawnDelayMs);
      }
    });

    // Destroy the bullet
    if (bullet && (bullet as any).destroy) {
      try { (bullet as any).destroy(); } catch {}
    }

    // Play death SFX
    try {
      const ctx = getGameContext(this.scene as any);
      ctx.sound?.playSfx(SFX.EnemyDeath, { volume: 0.8 });
    } catch { /* ignore sound errors */ }
  }) as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback;

  this.wireProjectileEnemyOverlap(projectiles, onProjectileHitEnemy);

  // Player vs enemy -> scene provides handler, Spawner wires the overlap
  this.wirePlayerEnemyOverlap(getPlayer, onPlayerHit);
}

/**
 * Helper to respawn a zombie-like enemy object after a delay at camera center.
 * Expects an Arcade-enabled game object with enableBody/disableBody methods.
 */
respawnZombieAtCameraCenter(zombieObj: Phaser.GameObjects.GameObject, delayMs = 10000): void {
  const anyZ = zombieObj as any;
  if(typeof anyZ.disableBody === 'function') {
  anyZ.disableBody(true, true);
} else {
  // fallback: hide/disable from scene if not an Arcade Sprite
  anyZ.setActive?.(false);
  anyZ.setVisible?.(false);
}

const cx = (this.scene.cameras?.main?.centerX) ?? 0;
const cy = (this.scene.cameras?.main?.centerY) ?? 0;

this.scene.time.delayedCall(delayMs, () => {
  if (typeof anyZ.enableBody === 'function') {
    anyZ.enableBody(true, cx, cy, true, true);
    // Make sure Arcade body is reconfigured similarly to spawn time
    try {
      anyZ.body.setAllowGravity(false);
      anyZ.body.setImmovable(false);
    } catch {}
  } else {
    anyZ.setPosition?.(cx, cy);
    anyZ.setActive?.(true);
    anyZ.setVisible?.(true);
  }
});
  }

/**
 * Convenience to return the created groups.
 */
getGroups(): SpawnedGroups {
  return {
    pistolPowerups: this.pistolPowerups,
    waterBottles: this.waterBottles,
    enemiesGroup: this.enemiesGroup,
  };
}
}